import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";

export const Aurora = ({ className = "" }) => (
  <div className={`aurora ${className}`} aria-hidden><span /><span /><span /></div>
);

export const Logo = ({ size = 34, withText = true }) => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5">
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden>
      <defs><linearGradient id="lg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#7A5CFF" /><stop offset="1" stopColor="#FF5C7A" /></linearGradient></defs>
      <rect width="64" height="64" rx="16" fill="#0D0B16" />
      <path d="M32 8 52 20v24L32 56 12 44V20z" fill="none" stroke="url(#lg)" strokeWidth="4" />
      <path d="M35 18 24 35h8l-3 12 12-18h-8z" fill="url(#lg)" />
    </svg>
    {withText && <span className="font-display text-lg font-bold tracking-[0.18em] text-white">FORGE</span>}
  </Link>
);

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Typewriter = ({ words, speed = 70, hold = 1600, className = "" }) => {
  const [i, setI] = useState(0);
  const [n, setN] = useState(0);
  const [del, setDel] = useState(false);
  useEffect(() => {
    const w = words[i % words.length];
    const t = setTimeout(() => {
      if (!del && n < w.length) setN(n + 1);
      else if (!del) setDel(true);
      else if (n > 0) setN(n - 1);
      else { setDel(false); setI(i + 1); }
    }, !del && n === w.length ? hold : del ? speed / 2 : speed);
    return () => clearTimeout(t);
  }, [n, del, i, words, speed, hold]);
  return <span className={className}>{words[i % words.length].slice(0, n)}<span className="caret" /></span>;
};

export const Magnetic = ({ children, className = "", strength = 0.25, ...rest }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const x = useSpring(useMotionValue(0), { stiffness: 220, damping: 15 });
  const y = useSpring(useMotionValue(0), { stiffness: 220, damping: 15 });
  const move = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * strength);
    y.set((e.clientY - r.top - r.height / 2) * strength);
  };
  return (
    <motion.div ref={ref} style={{ x, y }} onMouseMove={move} onMouseLeave={() => { x.set(0); y.set(0); }} className={`inline-block ${className}`} {...rest}>
      {children}
    </motion.div>
  );
};

export const FireButton = ({ children, className = "", as: As = "button", ...rest }) => (
  <As
    className={`group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-fire px-6 py-3 font-semibold text-white shadow-[0_10px_40px_-10px_rgba(255,92,122,0.7)] transition-[transform,box-shadow] duration-200 hover:shadow-[0_14px_50px_-8px_rgba(122,92,255,0.8)] active:scale-95 disabled:opacity-50 ${className}`}
    {...rest}
  >
    <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
    <span className="relative inline-flex items-center gap-2">{children}</span>
  </As>
);

export const GhostButton = ({ children, className = "", as: As = "button", ...rest }) => (
  <As className={`inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 font-semibold text-white backdrop-blur transition-colors duration-200 hover:border-white/30 hover:bg-white/10 ${className}`} {...rest}>
    {children}
  </As>
);
